import { queryClient } from '@/router'
import { useMutation } from '@tanstack/react-query'
import { type FC, useCallback } from 'react'
import { toast } from 'sonner'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/alert-dialog/alert-dialog'

import { deleteApplicantsMutation } from '@/lib/queries/business/v1/applicant/mutations'
import type { ResApplicantDetail } from '@/lib/queries/business/v1/types'
import { searchApplicantQueryKey } from '@/lib/queries/business/v2/applicant/search/queries'

export interface DeleteApplicantDialogProps {
  open: boolean
  applicant: Pick<ResApplicantDetail, 'path_id' | 'applicant_name' | 'email'>
  onDeleted: () => Promise<void>
  onClose: () => void
}

export const DeleteApplicantDialog: FC<DeleteApplicantDialogProps> = ({
  open,
  applicant,
  onDeleted,
  onClose,
}: DeleteApplicantDialogProps) => {
  const mutationDelete = useMutation(deleteApplicantsMutation)

  const handleCancel = useCallback(() => {
    onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleDelete = useCallback(() => {
    mutationDelete.mutate(
      { pathIds: [applicant.path_id] },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: searchApplicantQueryKey.base,
          })
          await onDeleted()
          onClose()
          toast.success('Applicant deleted successfully.')
        },
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicant.path_id])

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Deleting {applicant.applicant_name}
            <div className="mt-2 text-sm">{applicant.email}</div>
          </AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete this applicant? All associated cases will also be permanently deleted.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={mutationDelete.isPending} onClick={handleDelete}>
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
